import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { AmbientAudio } from './game/AmbientAudio';
import { Companion } from './game/Companion';
import { DialogueSystem } from './game/DialogueSystem';
import { HandBond } from './game/HandHold';
import { Input } from './game/Input';
import { MobileControls } from './game/MobileControls';
import { moodFromState, MusicSystem } from './game/MusicSystem';
import { Player } from './game/Player';
import { SoftCamera } from './game/SoftCamera';
import { Tutorial, TutorialStep } from './game/Tutorial';
import { AnimTester } from './game/AnimTester';
import { setupAtmosphere } from './game/Atmosphere';
import { buildWorld, createDust, WorldData } from './game/World';

const canvas = document.getElementById('game') as HTMLCanvasElement;
const titleOverlay = document.getElementById('title-overlay')!;
const startBtn = document.getElementById('start-btn')!;
const hud = document.getElementById('hud')!;
const promptEl = document.getElementById('prompt')!;
const tutorialEl = document.getElementById('tutorial')!;
const dialogueEl = document.getElementById('dialogue')!;
const lookHintEl = document.getElementById('look-hint')!;
const endingEl = document.getElementById('ending')!;
const roomChipEl = document.getElementById('room-chip')!;

const renderer = new THREE.WebGLRenderer({
  canvas,
  antialias: true,
  powerPreference: 'high-performance',
});
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight);

const scene = new THREE.Scene();
const atmosphere = setupAtmosphere(scene, renderer);

const loadingEl = document.createElement('div');
loadingEl.id = 'boot-loading';
loadingEl.textContent = 'Loading the keep…';
loadingEl.style.cssText =
  'position:fixed;inset:auto 0 18% 0;text-align:center;color:#e8dcc8;font:500 14px/1.4 Georgia,serif;letter-spacing:.08em;opacity:.85;pointer-events:none;z-index:5';
document.body.appendChild(loadingEl);

const world: WorldData = await buildWorld(scene);
loadingEl.remove();
const dust = createDust(scene, new THREE.Vector3(-0.55, 0.72, 0.35));

const cameraRig = new SoftCamera(window.innerWidth / window.innerHeight);
const gateLook = new THREE.Vector3();
world.gateMesh.getWorldPosition(gateLook);
gateLook.y = 1.6;
cameraRig.bindLevel(world.rooms, { r0: gateLook });
cameraRig.setOccluders(scene.getObjectByName('QuietKeep') ?? scene);
const input = new Input(canvas);
const mobileControls = new MobileControls(input);
const player = new Player(world.spawn, world.playBounds.killY);
const companion = new Companion(world.companionSpawn, world.playBounds);
companion.setSolids(world.solids);
const handBond = new HandBond();
scene.add(player.rig.root);
scene.add(companion.rig.root);
scene.add(handBond.root);

const handMid = new THREE.Vector3();
const playerHand = new THREE.Vector3();
const companionHand = new THREE.Vector3();

const playerLight = new THREE.PointLight(0xffd2a8, 0.65, 6, 2);
scene.add(playerLight);

const dialogue = new DialogueSystem(dialogueEl);
void dialogue.load();

const music = new MusicSystem();
void music.loadCatalog();
const audio = new AmbientAudio();

const tutorial = new Tutorial(tutorialEl, (step: TutorialStep) => {
  if (step === 'done') return;
  dialogue.trigger(`tutorial:${step}`);
});

const animTester = new AnimTester();
void player.rig.whenReady().then((ok) => {
  if (ok) animTester.setClips(player.rig.listClipNames());
});
void companion.rig.whenReady();

const composer = new EffectComposer(renderer);
composer.addPass(new RenderPass(scene, cameraRig.camera));
const bloom = new UnrealBloomPass(
  new THREE.Vector2(window.innerWidth, window.innerHeight),
  0.35,
  0.65,
  0.82,
);
composer.addPass(bloom);

const gradeShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    uLift: { value: new THREE.Vector3(0.02, 0.015, 0.03) },
    uGain: { value: new THREE.Vector3(1.05, 0.98, 0.9) },
    uVignette: { value: 0.35 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform vec3 uLift;
    uniform vec3 uGain;
    uniform float uVignette;
    varying vec2 vUv;
    void main() {
      vec4 c = texture2D(tDiffuse, vUv);
      c.rgb = c.rgb * uGain + uLift;
      float d = distance(vUv, vec2(0.5));
      c.rgb *= smoothstep(0.95, 0.35, d * (0.65 + uVignette));
      gl_FragColor = c;
    }
  `,
};
composer.addPass(new ShaderPass(gradeShader));
composer.addPass(new OutputPass());

let running = false;
let gateOpen = false;
let gateAnim = 0;
let gateBaseY = 0;
let wasClimbing = false;
let storyStarted = false;
let nearLeverLatched = false;
let nearCompanionLatched = false;
let endingPlayed = false;
let wasAirborne = false;
let gateDustT = 0;

// Title-screen bed on first gesture over the overlay
let titleBedStarted = false;
const kickTitleBed = () => {
  if (titleBedStarted || running) return;
  titleBedStarted = true;
  void music.startTitlePreview();
  void audio.startTitleBed();
};
titleOverlay.addEventListener('pointerdown', kickTitleBed, { once: true });
startBtn.addEventListener('pointerdown', kickTitleBed, { once: true });

startBtn.addEventListener('click', async () => {
  titleOverlay.classList.add('fade-out');
  hud.classList.remove('hidden');
  running = true;
  canvas.focus();
  mobileControls.syncVisibility(true);
  if (input.wantsPointerLock()) {
    input.requestLock();
    window.setTimeout(() => {
      if (running && !input.pointerLocked) input.requestLock();
    }, 120);
  } else {
    lookHintEl.classList.add('hidden');
    lookHintEl.textContent = 'Drag right side to look · stick to move';
  }
  await music.start('courtyard');
  await audio.start();
  if (!storyStarted) {
    storyStarted = true;
    dialogue.trigger('start');
    window.setTimeout(() => {
      if (tutorial.getStep() === 'look') tutorial.announceCurrent();
    }, 7000);
  }
});

canvas.addEventListener('click', () => {
  if (running && input.wantsPointerLock()) {
    input.requestLock();
    lookHintEl.classList.add('hidden');
  }
});

document.addEventListener('pointerlockchange', () => {
  if (document.pointerLockElement !== canvas) {
    input.consumeMouse();
    if (running && !endingPlayed && input.wantsPointerLock()) {
      lookHintEl.classList.remove('hidden');
    }
  } else {
    lookHintEl.classList.add('hidden');
  }
});

window.addEventListener('resize', () => {
  const w = window.innerWidth;
  const h = window.innerHeight;
  renderer.setSize(w, h);
  composer.setSize(w, h);
  bloom.setSize(w, h);
  cameraRig.setSize(w, h);
});

let leverPullT = 0;
let leverPulling = false;
const LEVER_ARM_UP = 0.72;
const LEVER_ARM_DOWN = -0.95;

function openGate(): void {
  if (gateOpen) return;
  gateOpen = true;
  gateBaseY = world.gateMesh.position.y;
  // Drop colliders overlapping the gate leaf
  world.gateMesh.updateMatrixWorld(true);
  const gb = new THREE.Box3().setFromObject(world.gateMesh);
  world.solids = world.solids.filter((s) => {
    const overlap =
      s.maxX > gb.min.x &&
      s.minX < gb.max.x &&
      s.maxY > gb.min.y &&
      s.minY < gb.max.y &&
      s.maxZ > gb.min.z &&
      s.minZ < gb.max.z;
    const sizeY = s.maxY - s.minY;
    const sizeX = s.maxX - s.minX;
    const sizeZ = s.maxZ - s.minZ;
    const looksLikeGate = overlap && sizeY > 1.5 && Math.min(sizeX, sizeZ) < 2.2;
    return !looksLikeGate;
  });
  tutorial.noteGate();
  dialogue.trigger('moment:gate');
  music.setMood('hope');
  audio.playGate();
  gateDustT = 2.8;
  window.setTimeout(() => playEnding(), 4200);
}

function playEnding(): void {
  if (endingPlayed) return;
  endingPlayed = true;
  endingEl.classList.remove('hidden');
  // Force reflow so transition runs
  void endingEl.offsetWidth;
  endingEl.classList.add('visible');
  lookHintEl.classList.add('hidden');
  dialogue.trigger('moment:ending');
}

function currentRoomName(): string {
  const p = player.position;
  for (const r of world.rooms) {
    if (p.x >= r.minX && p.x <= r.maxX && p.z >= r.minZ && p.z <= r.maxZ) {
      return r.name;
    }
  }
  return '';
}

function updatePrompt(): void {
  if (!running) {
    promptEl.classList.add('hidden');
    return;
  }
  const roomName = currentRoomName();
  if (roomName && roomChipEl.textContent !== roomName) {
    roomChipEl.textContent = roomName;
  }

  const interact = player.nearestInteractable(world.interactables);
  if (interact) {
    promptEl.textContent = interact.prompt;
    promptEl.classList.remove('hidden');
    return;
  }
  if (!companion.isHeld()) {
    const d = player.position.distanceTo(companion.position);
    if (d < 3.4) {
      promptEl.textContent = 'Press E — take their hand';
      promptEl.classList.remove('hidden');
      return;
    }
  } else if (!player.climbing) {
    promptEl.textContent = 'Press E — let go';
    promptEl.classList.remove('hidden');
    return;
  }
  const climb = player.getNearClimb();
  if (climb && !player.climbing) {
    promptEl.textContent = 'Press E or walk into the ladder · W/S to climb';
    promptEl.classList.remove('hidden');
    return;
  }
  promptEl.classList.add('hidden');
}

/** @returns true if E was spent on lever / companion. */
function handleInteract(justPressed: boolean): boolean {
  if (!justPressed) return false;

  const interact = player.nearestInteractable(world.interactables);
  if (interact && interact.id === 'lever') {
    interact.used = true;
    interact.prompt = '';
    leverPulling = true;
    player.rig.setState('pull');
    companion.rig.setState('point');
    audio.playLever();
    // Dim the warm “pull me” glow on the knob
    const knob = world.leverArm.getObjectByName('LeverKnob') as THREE.Mesh | undefined;
    if (knob) {
      const m = knob.material as THREE.MeshStandardMaterial;
      m.emissiveIntensity = 0.04;
    }
    dialogue.trigger('moment:lever');
    window.setTimeout(() => openGate(), 520);
    window.setTimeout(() => {
      player.rig.setState('victory');
      companion.rig.setState('clap');
    }, 1100);
    return true;
  }

  if (companion.isHeld()) {
    // Toggle release (ladder/lever already claimed E above).
    companion.setHeld(false);
    player.setCompanionHeld(false);
    handBond.setActive(false);
    player.rig.setHandIkTarget(null);
    companion.rig.setHandIkTarget(null);
    music.setMood(moodFromState({
      x: player.position.x,
      z: player.position.z,
      held: false,
      gateOpen,
      nearCompanion: false,
      nearLever: false,
      climbing: player.climbing,
    }));
    return true;
  }

  if (player.position.distanceTo(companion.position) < 3.6) {
    companion.setHeld(true);
    player.setCompanionHeld(true);
    player.rig.beginTakeHand();
    companion.rig.beginTakeHand();
    handBond.setActive(true);
    tutorial.noteHand();
    dialogue.trigger('moment:hand');
    music.setMood('companion');
    return true;
  }

  return false;
}

function updateStoryHints(): void {
  const nearCompanion =
    !companion.isHeld() && player.position.distanceTo(companion.position) < 3.2;
  const lever = world.interactables.find((i) => i.id === 'lever' && !i.used);
  const leverPos = world.leverPivot.position;
  const nearLever =
    !!lever &&
    player.position.distanceTo(new THREE.Vector3(leverPos.x, player.position.y, leverPos.z)) < 3.5;

  if (nearCompanion && !nearCompanionLatched && tutorial.getStep() === 'companion') {
    nearCompanionLatched = true;
    dialogue.hint('near:companion');
  }
  if (!nearCompanion) nearCompanionLatched = false;

  if (nearLever && !nearLeverLatched && (tutorial.getStep() === 'gate' || companion.isHeld())) {
    nearLeverLatched = true;
    dialogue.hint('near:lever');
  }
  if (!nearLever) nearLeverLatched = false;

  const mood = moodFromState({
    x: player.position.x,
    z: player.position.z,
    held: companion.isHeld(),
    gateOpen,
    nearCompanion,
    nearLever,
    climbing: player.climbing,
  });
  music.setMood(mood);
}

const clock = new THREE.Clock();

function tick(): void {
  requestAnimationFrame(tick);
  const dt = Math.min(0.05, clock.getDelta());

  const mouse = input.consumeMouse();
  if (running && (input.pointerLocked || mouse.dx !== 0 || mouse.dy !== 0)) {
    cameraRig.orbit(mouse.dx, mouse.dy);
  }

  if (running) {
    const test = animTester.poll();
    if (test.exit) {
      player.rig.clearTestLock();
    } else if (test.clip) {
      player.rig.playClipByName(test.clip);
    }

    const interactEdge = input.interactJustPressed();
    const interactUsed = handleInteract(interactEdge);
    const ladderMount = interactEdge && !interactUsed;

    player.update(dt, input, cameraRig.yaw, world.solids, world.climbables, ladderMount);
    const playerHoriz = Math.hypot(player.velocity.x, player.velocity.z);
    companion.update(
      dt,
      player.position,
      player.yaw,
      playerHoriz,
      input.running() && playerHoriz > 0.35,
    );

    // Distance break if somehow separated while held.
    if (
      companion.isHeld() &&
      player.position.distanceTo(companion.position) > 4.5
    ) {
      companion.setHeld(false);
      player.setCompanionHeld(false);
      handBond.setActive(false);
      player.rig.setHandIkTarget(null);
      companion.rig.setHandIkTarget(null);
    }

    // Meet hands in world space after both poses update.
    if (companion.isHeld()) {
      if (player.climbing) {
        companion.setHeld(false);
        player.setCompanionHeld(false);
        handBond.setActive(false);
        player.rig.setHandIkTarget(null);
        companion.rig.setHandIkTarget(null);
      } else {
        player.rig.getHandWorldPosition('left', playerHand);
        companion.rig.getHandWorldPosition('right', companionHand);
        handMid.lerpVectors(playerHand, companionHand, 0.5);
        player.rig.setHandIkTarget(handMid);
        companion.rig.setHandIkTarget(handMid);
        player.rig.refreshHandIk();
        companion.rig.refreshHandIk();
        player.rig.getHandWorldPosition('left', playerHand);
        companion.rig.getHandWorldPosition('right', companionHand);
        handBond.setActive(true);
        handBond.update(playerHand, companionHand, performance.now() * 0.004);
      }
    } else {
      handBond.setActive(false);
    }

    playerLight.position.set(player.position.x, player.position.y + 1.6, player.position.z);

    const axis = input.axis();
    const moving = Math.hypot(axis.x, axis.z) > 0.1;
    const airborne = !player.onGround && !player.climbing;
    cameraRig.update(dt, player.position, {
      companion: companion.isHeld() ? companion.position : null,
      climbing: player.climbing,
      airborne,
      zoom: input.zooming(),
    });

    audio.updateFootsteps(dt, {
      grounded: player.onGround,
      speed: playerHoriz,
      climbing: player.climbing,
      running: input.running(),
    });
    if (wasAirborne && player.onGround && !player.climbing) audio.playLand();
    wasAirborne = airborne;

    tutorial.noteLook(dt, Math.abs(mouse.dx) + Math.abs(mouse.dy) > 0.5);
    tutorial.noteMove(moving);

    if (input.jumpPressed() && player.onGround) tutorial.noteJump();
    if (player.climbing && !wasClimbing) tutorial.noteClimb();
    wasClimbing = player.climbing;

    // Past the open gate (far side from spawn) → ending
    if (gateOpen && !endingPlayed) {
      const gp = new THREE.Vector3();
      world.gateMesh.getWorldPosition(gp);
      const sx = world.spawn.x - gp.x;
      const sz = world.spawn.z - gp.z;
      const px = player.position.x - gp.x;
      const pz = player.position.z - gp.z;
      const dist = Math.hypot(px, pz);
      if (dist < 6.5 && sx * px + sz * pz < 0) playEnding();
    }

    updatePrompt();
    updateStoryHints();
    dialogue.noteIdle(dt, tutorial.getStep() !== 'look');
    dialogue.update(dt);
    music.update(dt);
  } else {
    // Title idle — distant crane over the courtyard (ICO menu mood).
    const t = clock.elapsedTime;
    cameraRig.camera.position.set(
      10 + Math.sin(t * 0.1) * 2.5,
      10.5 + Math.sin(t * 0.16) * 0.4,
      22 + Math.cos(t * 0.08) * 2.2,
    );
    cameraRig.camera.lookAt(0, 1.1, 1.5);
  }

  if (gateOpen) {
    gateAnim = Math.min(1, gateAnim + dt * 0.55);
    world.gateMesh.position.y = gateBaseY + gateAnim * 4.2;
  }

  if (leverPulling) {
    leverPullT = Math.min(1, leverPullT + dt * 1.35);
    const ease = 1 - Math.pow(1 - leverPullT, 3);
    world.leverArm.rotation.z = LEVER_ARM_UP + (LEVER_ARM_DOWN - LEVER_ARM_UP) * ease;
  }

  // Drift dust (+ brief gate burst)
  const pos = dust.geometry.attributes.position as THREE.BufferAttribute;
  if (gateDustT > 0) gateDustT -= dt;
  const gx = world.gateMesh.position.x;
  const gz = world.gateMesh.position.z;
  for (let i = 0; i < pos.count; i++) {
    let y = pos.getY(i) + dt * (0.12 + (i % 5) * 0.02);
    if (y > 16) y = 0.5;
    if (gateDustT > 0 && i % 7 === 0) {
      pos.setX(i, gx + (Math.random() - 0.5) * 3.5);
      pos.setZ(i, gz + (Math.random() - 0.5) * 2.2);
      y = 0.4 + Math.random() * 3.5;
    }
    pos.setY(i, y);
  }
  pos.needsUpdate = true;

  atmosphere.update(cameraRig.camera);
  composer.render();
}

tick();
